import React from "react";
import {
  Alert,
  Box,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
  CircularProgress,
} from "@mui/material";
import { ModernConnector, ModernStepIcon } from "./steps/stepIcons";
import { getErrorMessage } from "../../utils/getErrorMessage";
import StepFooterActions from "./StepFooterActions";
import { useLeadCreationForm } from "./hooks/use-lead-creation-form";
import { STEPS } from "./config/stepsConfig";

const paperSx = {
  borderRadius: 3,
  border: "1px solid #E6ECEA",
  boxShadow: "0 1px 2px rgba(16,24,22,0.04)",
  p: { xs: 2, md: 4 },
};

const LeadCreationStepper = () => {
  const {
    activeStep,
    formData,
    leadData,
    setLeadData,
    showErrors,
    leadId,
    stepErrors,
    setStepErrors,
    setPhoneCheckStatus,
    isInitializing,
    isLoadingLead,
    isLeadFetchError,
    leadFetchError,
    isError,
    error,
    uploadError,
    currentStep,
    currentStepData,
    isLastStep,
    isBusy,
    isSubmitting,
    isSaveBtnDisabled,
    isSubmitBtnDisabled,
    nextButtonLabel,
    loadingLabel,
    updateStepData,
    handleNext,
    handleBack,
    handleSubmit,
    saveFinancierSelection,
    clearFieldError,
  } = useLeadCreationForm();

  if (isInitializing) {
    return (
      <Paper sx={paperSx}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            py: 6,
          }}
        >
          <CircularProgress size={28} sx={{ color: "#0E7A6E" }} />
          <Typography sx={{ fontSize: 13, color: "#66766F", fontWeight: 500 }}>
            Loading lead details…
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (isLeadFetchError) {
    return (
      <Paper sx={paperSx}>
        <Typography sx={{ fontSize: 13, color: "#B3261E" }}>
          {leadFetchError?.response?.data?.message ||
            leadFetchError?.message ||
            "Failed to load lead."}
        </Typography>
      </Paper>
    );
  }

  const StepComponent = currentStep.Component;

  return (
    <Paper sx={paperSx}>
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        connector={<ModernConnector />}
      >
        {STEPS.map((step) => (
          <Step
            key={step.key}
            sx={{
              minWidth: { xs: 0, sm: "auto" },
              flex: { xs: "1 1 0", sm: 1 },
              px: { xs: 0.25, sm: 1 },
            }}
          >
            <StepLabel
              StepIconComponent={ModernStepIcon}
              sx={{
                "& .MuiStepLabel-label": {
                  fontSize: { xs: "clamp(9px, 3vw, 12px)", sm: 13 },
                  overflowWrap: "normal",
                  wordBreak: "normal",
                  fontWeight: 600,
                  color: "#66766F",
                  mt: 1,
                  "&.Mui-active": { color: "#12181A", fontWeight: 700 },
                  "&.Mui-completed": { color: "#12181A" },
                },
              }}
            >
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {(isError || uploadError) && (
        <Alert severity="error" sx={{ borderRadius: 2, my: 3 }}>
          {uploadError ||
            getErrorMessage(
              error,
              "Something went wrong while saving the lead.",
            )}
        </Alert>
      )}

      <Box sx={{ mb: 5, mt: uploadError || isError ? 3 : 8 }}>
        <StepComponent
          data={currentStepData}
          onChange={updateStepData}
          showErrors={showErrors}
          errors={stepErrors}
          setErrors={setStepErrors}
          onPhoneCheckStatusChange={setPhoneCheckStatus}
          allFormData={formData}
          leadId={leadId}
          leadData={leadData}
          setLeadData={setLeadData}
          onSaveFinancier={saveFinancierSelection}
          clearFieldError={clearFieldError}
        />
      </Box>

      <StepFooterActions
        activeStep={activeStep}
        onBack={handleBack}
        onNext={handleNext}
        isLastStep={isLastStep}
        loading={isBusy}
        isSubmitting={isSubmitting}
        loadingLabel={loadingLabel}
        disabled={isSaveBtnDisabled}
        submitDisabled={isSubmitBtnDisabled}
        nextLabel={nextButtonLabel}
        onSubmit={handleSubmit}
        isBackDisabled={
          leadData?.isFinancierChangeRequest || leadData?.loanSummary?.financier
        }
      />
    </Paper>
  );
};

export default LeadCreationStepper;